import { getCurrencyScaleAndFormatterForValues } from "helpers/formatters/currency-scale"
import { buildScaledCurrencyLabel } from "helpers/metrics/currency-scale-label"
import { unwrapValue } from "./unwrapValue"

type ColumnLabel = string | ((isActive: boolean) => string)
type CellGetter = (row: unknown, path: string | undefined, columnId: string) => unknown

export interface ScalableColumn {
  id: string
  path?: string
  label: ColumnLabel
  formatter?: (value: unknown) => string
  getter?: CellGetter
  isDollarColumn?: boolean
}

export interface ColumnCategory {
  getter?: CellGetter
  columns: ScalableColumn[]
}

function buildScaledLabel(label: ColumnLabel, scale: string): ColumnLabel {
  return typeof label === "function"
    ? (isActive: boolean) => buildScaledCurrencyLabel(label(isActive), scale)
    : buildScaledCurrencyLabel(label, scale)
}

function readColumnValues(column: ScalableColumn, categoryGetter: CellGetter | undefined, rows: readonly unknown[]) {
  const getter = column.getter ?? categoryGetter
  if (!getter) return []
  return rows.map(row => unwrapValue(getter(row, column.path || column.id, column.id)))
}

function scaleColumn(column: ScalableColumn, categoryGetter: CellGetter | undefined, rows: readonly unknown[]): ScalableColumn {
  if (!column.isDollarColumn) return column

  const { currencyScale, formatter } = getCurrencyScaleAndFormatterForValues(readColumnValues(column, categoryGetter, rows))
  return {
    ...column,
    label: buildScaledLabel(column.label, currencyScale),
    formatter,
  }
}

export function applyColumnScales<TCategory extends ColumnCategory>(
  columnCategories: readonly TCategory[],
  rows: readonly unknown[],
): TCategory[] {
  return columnCategories.map(category => ({
    ...category,
    columns: category.columns.map(column => scaleColumn(column, category.getter, rows)),
  }))
}
